import { Connection, PublicKey } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from '@solana/spl-token';

import { getEnhancedTokenMetadata } from './tokenMetadata';
import { getValidImageUrl } from './imageValidator';

import config from '@/config';
import { logger } from '@/lib/logger';

/**
 * Detect network type from RPC URL
 */
function getNetworkFromRpcUrl(rpcUrl: string): 'mainnet' | 'devnet' {
  return rpcUrl.includes('devnet') ? 'devnet' : 'mainnet';
}

// Wrapped SOL mint address - exclude this from burnable assets
const WRAPPED_SOL_MINT = 'So11111111111111111111111111111111111111112';
const ENABLE_PNFT_JUNKING = Boolean(config.ENABLE_PNFT_JUNKING);

// Blacklisted tokens that cannot be burned
const BLACKLISTED_TOKENS = [
  'So11111111111111111111111111111111111111112', // SOL (wrapped)
  'pSoLmb1L6mJUFPxDYtxZ9fYFktGdFUMGTGKrSPxmzEG', // PSOL (Phantom Staked SOL)
  'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', // USDT
  'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', // USDC
  'LinkhB3afbBKb2EQQu7s7umdZceV3wcvAUJhQAfQ23L', // LINK
  'WLFinEv6ypjkczcS83FZqFpgFZYwQXutRbxGe7oC16g', // WLFI
  '5XZw2LKTyrfvfiskJ78AMpackRjPcyCif1WhUsPDuVqQ', // WBTC
  'DEkqHyPN7GMRJ5cArtQFAWefqbZb33Hyf6s5iCwjEonT', // USDe
  'USDSwr9ApdHk5bvJKMjzff41FfuX8bSxdKcR81vTwcA', // USDs
  '6p6xgHyF7AeE6TZkSmFsko444wqoP15icUSqi2jfGiPN', // TRUMP
  'pumpCmXqMfrsAkQ5r49WcJnRayYRqmXz6ae8H7H9Dfn', // PUMP
  'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN', // JUP
  'USD1ttGY1N17NEEHLmELoaybftRBUSErhqYiQzvEmuB', // USD1
  'J1toso1uCk3RLmjorhTtrVwY9HJ7X8V9yYac6Y7kGCPn', // JITOSOL
  '2b1kV6DkPAnxd5ixfnxCpjxmKwqjjaYmCZfHsFu24GXo', // PYUSD
  'J6pQQ3FAcJQeWPPGppWRb4nM8jU3wLyYbRrLh7feMfvd', // 2Z
  '2Wu1g2ft7qZHfTpfzP3wLdfPeV1is4EwQ3CXBfRYAciD', // GOHOME
  'BNso1VUJnh4zcfpZa6986Ea66P6TCp59hvtNJ8b1X85', // BNSOL
  '2zMMhcVQEXDtdE6vsFS7S7D5oUodfJHE8vd1gnBouauv', // PENGU
  '3iQL8BFS2vE7mww4ehAqQHAsbmRNCrPxizWAT2Zfyr9y', // VIRTUALS
  'AvZZF1YaZDziPY2RCK4oJrRVrbN3mTD9NL24hPeaZeUj', // SYRUPUSDC
  'rndrizKT3MK1iimdxRdWabcF7Zg7AR5T4nud4EkHBof', // RENDER
  'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263', // BONK
  'Dm5BxyMetG3Aq5PaG1BrG7rBYqEMtnkjvPNMExfacVk7', // ATH
  'HZ1JovNiVvGrGNiiYvEozEVgZ58xaU3RKwX8eACQBCt3', // PYTH
  'jtojtomepa8beP8AuQc6eXt5FriJwfFMwQx2v2f9mCL', // JTO
  '4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R', // RAY
  'J3NKxxXZcnNiMjKw9hYb2K4LUxgwB6t1FtPtQVsv3KFr', // SPX
  'SarosY6Vscao718M4A778z4CGtvcwcGef5M9MEH1LGL', // SAROS
  '85VBFQZC9TZkfaptBWjvUw7YbZjy52A6mjtPGjstQAmQ', // W
  'KMNo3nJsBXfcpJTVhZcXLW7RmTwTt4GVFE7suUBo9sS', // KMNO
  'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm', // WIF
  'hntyVP6YFm1Hg25TN9WGLqM12b8TQmcknKrdu1oxWux', // HNT
  'DuEy8wWrzCUun5ZbbG9hkVqXqqicpTQw8gB7nEAzpCHQ', // FLUID
  'METvsvVRapdj9cFLzq4Tr43xK4tAjQfwX76z3n6mWQL', // MET
  '3dQTr7ror2QPKQ3GbBCokJUmjErGg8kTJzdnYjNfvi3Z', // BORG
  '7dUKUopcNWW6CcU4eRxCHh1uiMh32zDrmGf6ufqhxann', // BABYDOGE
  'DriFtupJYLTosbwoN8koMbEYSx54aFAVLddWsbksjwg7', // DRIFT
  'Grass7B4RdKfBCjTKgSqnXkqjwiGvQyFbuSCUJr3XXjs', // GRASS
  '9BB6NFEcjBCtnNLFko2FqVQBq8HHM13kCyYcdQbgpump', // FARTCOIN
  'ZBCNpuD7YMXzTHB2fhGkGi78MNsHGLRXUhRewNRm9RU', // ZBCN
  'DBRiDgJAMsM95moTzJs7M9LnkGErpbv9v6CUR1DXnUu5', // DBR
  'Dz9mQ9NzkBcCsuGPFJ3r1bS4wgqKMHBPiVuniW8Mbonk', // USELESS
  'WMTXyYKUMTG3VuZA5beXuHVRLpyTwwaoP7h2i8YpuRH', // WMTX
  'bioJ9JTqW62MLz7UKHU69gtKhPpGi1BQhccj2kmSvUJ', // BIO
  'EPeUFDgHRxs9xxEPVaL6kfGQvCon7jmAWKVUHuux1Tpz', // BAT
  'METAewgxyPbgwsseH8T16a39CQ5VyVxZi9zXiDPY18m', // MPLX
  'vQoYWru2pbUdcVkUrRH74ktQDJgVjRcDvsoDbUzM5n9', // REKT
  'MEW1gQWJ3nEXg2qgERiKu7FAFj79PHvQVREQUzScPP5', // MEW
  '7GCihgDB8fe6KNjn2MYtkzZcRjQy3t9GHdC8uHYmW2hr', // POPCAT
  'Hg8bKz4mvs8KNj9zew1cEF9tDw1x2GViB4RFZjVEmfrD', // TDCCP
  '2qEHjDLDLbuBgRYvsxhc5D6uDWAivNFZGan56P1tpump', // PNUT
  '4Njvi3928U3figEF5tf8xvjLC5GqUN33oe4XTJNe7xXC', // T
  '6LX8BhMQ4Sy2otmAWj7Y5sKd9YTVVUgfMsBzT6B9W7ct', // FRAX
  'ED5nyyWEzpPPiWimP8vYm7sD7TD3LAt3Q3gRTWHzPJBY', // MOODENG
  'FUAfBo2jgks6gB4Z4LfZkqSZgzNucisEHqnNebaRxM1P', // MELANIA
  'GqXX9MfkURBZ5cFym9HDzqTL7uZkjtCSqLkUSe2xpump', // MARSMI
  '5Y8NV33Vv7WbnLfq3zBcKSdYPrk7g2KoiQoe7M2tcxp5', // ONYC
  'BCNT4t3rv5Hva8RnUtJUJLnxzeFAabcYp8CghC1SmWin', // BC
  'FeR8VBqNRSUD5NtXAj2n3j1dAHkZHfyDktKuLXD4pump', // JELLYJELLY
  'DtR4D9FtVoTX2569gaL837ZgrB6wNjj6tkmnX9Rdk9B2', // AURA
  'Ey59PH7Z4BFU4HjyKnyMdWt5GGN76KazTAwQihoUXRnk', // LAUNCHCOIN
  'MEFNBXixkEbait3xn9bkm8WsJzXtVsaJEn4c8Sam21u', // ME
  '7i5KKsX2weiTkry7jA4ZwSuXGhs5eJBEjY8vVxR4pfRx', // GMT
  'BZLbGTNCSFfoth2GYDtwr7e4imWzpR5jqcUuGEwr646K', // IO
  'ukHH6c7mMyiWCf1b9pnWe25TSpkDDt3H5pQZgZ74J82', // BOME
  'DvjbEsdca43oQcw2h3HW1CT7N3x5vRcr3QrvTUHnXvgV', // DOOD
  '63LfDmNb3MQ8mw9MtZ2To9bEA2M71kZUUGq5tiJxcqj9', // GIGA
  'MNDEFzGvMt87ueuHvVU9VcTqsAP5b3fTGPsHuuPA5ey', // MNDE
  'LAYER4xPpTCb3QL8S9u41EAhAX7mhBn8Q6xMTwY2Yzc', // LAYER
  'CzLSujWBLFsSjncfkh59rUFqvafWcY5tzedWJSuypump', // GOAT
  'DrZ26cKJDksVRWib3DVVsjo9eeXccc7hKhDJviiYEEZY', // YZY
  '4vMsoUT2BWatFweudnQM1xedRLfJgJ7hswhcpz4xgBTy', // HONEY
  '6AJcP7wuLwmRYLBNbi825wgguaPsWzPBEHcHndpRpump', // VINE
  'BenJy1n3WTx9mTjEvy63e8Q1j4RqUc6E4VBMz3ir4Wo6', // Legacy Perena
  'HUMA1821qVDKta3u2ovmfDQeW2fSQouSKE8fkF44wvGw', // HUMA
];

// Tokens completely excluded from dust swapping (hidden from swap UI entirely)
export const SWAP_BLACKLISTED_TOKENS = new Set([
  'So11111111111111111111111111111111111111112', // SOL (wrapped)
  'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', // USDT
  'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263', // BONK
]);

/**
 * Protected NFT collections that cannot be burned (high-value blue chip collections)
 * Must be kept in sync with backend PROTECTED_NFT_COLLECTIONS in constants/index.ts
 */
const PROTECTED_NFT_COLLECTIONS = new Set([
  // Blue Chip Collections (sorted by market cap)
  'J1S9H3QjnRtBbbuD4HjPV6RpRhwuk4zKbxsnCHuTgh9w', // Mad Lads
  '6mszaj17KSfVqADrQj3o4W3zoLMTykgmV37W4QadCczK', // Claynosaurz
  'SMBtHCCC6RYRutFEPb4gZqeBLUZbMNhRKaMKZZLHi7W', // SMB Gen 2
  '6XxjKYFbcndh2gDcsUrmZgVEsoDxXMnfsaGY6fpTJzNr', // DeGods
  'BiwemBos3Su9QcNUiwkZMbSKi7m959t5oVpmPnM9Z3SH', // Lifinity Flares
  'J6RJFQfLgBTcoAt3KoZFiTFW9AbufsztBNDgZ7Znrp1Q', // Galactic Geckos
  'BUjZjAS2vbbb65g7Z1Ca9ZRVYoJscURG5L3AkVvHP9ac', // Famous Fox Federation
  'HJx4HRAT3RiFq7cy9fSrvP92usAmJ7bJgPccQTyroT2r', // Taiyo Robotics
  '4mKSoDDqApmF1DqXvVTSL6tu2zixrSSNjqMxUnwvVzy2', // y00ts
  'HF6SFg5RkWNQrEhmnXV7H8EmLPxg3jDaggEni1SMVAi6', // Degenerate Ape Academy
  '69k55dCTwiUPNgaTZ8FVMADorTvEGJEGuAGEB7m1qB1S', // BoDogos
  '8Rt3Ayqth4DAiPnW9MDFi63TiQJHmohfTWLMQFHi4KZH', // SMB Gen 3
  '5PA96eCFHJSFPY9SWFeRJUHrpoNF5XZL6RrE1JADXhxf', // Tensorians
  '9Z7JFLZikV7PYS4kffDZgUqyLZNoQNxBmyF98vx1j51L', // Moonster
  'P2bPqREHLN3BpdRjKJeE1nRNKY72xdEUcdRH2V1x1ps', // Player 2
  '3saAedkM9o5g1u5DCqsuMZuC4GRqPB4TuMkvSsSVvGQ3', // Okay Bears
  'GWuSqaw2aqob4KeFyKgDKnLWre4ZjV8FHSPR2W3c4xJr', // Aurorians
  '2usbcPABgseRHwff6HvdbtknfDJsnmRGPQd4CJtcT2Sy', // Sol Decoder
  'PSP1JmwMdd3usfyYMXTXfnRNs4e1M1UeVo7hQRaFmLu', // Play Solana NFT
  'GNf2XFFtukU35boMK8mSQ9QL9nSDRTKYENtTGAmnrikj', // Gamba Dogs
  'HxSsfM9WxQWj79chAUNL6osZxQjJj5iMUwrjEfRBvYBR', // Quack Heads
  'DUX8SZXLKigc84BBUcYjA7PuKe2SFwXFtQVgwmBsaXKm', // Retardio Cousins
  'Dn5qsJJj4mKBdYQiXFfrS5PSNPwAakK6XSpvAxqH6v2C', // Meatbags
  'FhMPFCH2rXfkQ2AvnjuJUiBej8jgHH9D43DDkBTTL4MM', // Claynosaurz: Clay
  'pyTh2UtBKfuDW6KCdT3swospYeoLmmKaGujWA91Moru', // Pythenians
  'CjL5WpAmf4cMEEGwZGTfTDKWok9a92ykq9aLZrEK2D5H', // Little Swag World
  'GokAiStXz2Kqbxwz2oqzfEXuUhE7aXySmBGEP7uejKXF', // Saga Monkes
  'HbPtffcEzzSzZ1VJaTjaAJqTUQpfNeMkPqNG81dWE2Bi', // Transdimensional Fox Federation
  'CRittErsK51xrVktQeMeAW2cK7JNqv6Ko9Zrw26eF2cT', // Critters Quest
  'eAeeYRC6J9e8v95QvWvgAWHz3ko7u5BR7wEqzPmYNnE', // Entropy Acolytes
  'EwxYgrffpuTuNa4C1b4xxrEkRbZAQgMG5fAiY3uJVZoH', // Wassieverse
  '686ySfiH4RDLygPHzkhmmJ7eZcdc9Mt6jCKhVSRbymkM', // Portals
  'Dz8cv979gvaabybEhV3sSuDxJV9bqqehvt3BRSgPoRW3', // ICM Investor Centre Cult
  '6a5FuaxdKmhjm5GnTXPcJnqCqFftvho2E5Wo7N7diXtx', // GAINZ
  '8NM2pYYznbZTNPRpp6g2WmTX7Lo2c6EBiv3iaF3kwxtt', // Mosciun
  '3pAx1gCrmcVFfGdVFRFaaqDEFq7ngung3nD3Q6mzs18x', // Sol Slugs
  'Axm6tfLD9WYoddtvwjdhRnB9XtdqAVzrZxKuvX8K5GdV', // Market Elites
  '7hzkZbCtEZKYDLd1iTHgrtyA6nSptARJfq7BLVB3aRe9', // Whal3s
  'CLBrjmoDCFvrW8ukxXtDrBXWkL7Yx8PKspKaBPFxhpmL', // Smyths
  'C7on9fL8YFp5W6M7a6SvehMKBppauZXu2eYDTZG4BN2i', // Frogana
  'F46bRqniGzuNWB7QPnrzAUYoLNYNC5TUCZFySdhYoVQM', // MinTechSOL
  'Dzmn3vDKMKrBkjSg28ZHxn9VUjV32w1hPspxyBFSscdg', // Zero Monke Biz
  'G86H6xCohHXe8MRbZvfkrm2gTgp6rH8PqAPnDLhUhPab', // Haxz
  '71tyVbvaxjuQze8wXG7Gxsc8HRPnehX349VcGFzKskZA', // Mavrix by Jelly Co
  'aLs8rXD8NoYwgnCFzBofsGdwaocePAkzT1UFX1WmwaR', // Bored Ape Solana Club
  '2kEAck1FyW8TxB5SprEnasb4gkaahTdDV83wPtxm9y32', // Cyber Frogs
  'EComfdQdGnNExXDKV7dLgbCv9nxSTfZRHrCUqe43inCk', // GOLFN Season 1
  '5f2PvbmKd9pRLjKdMr8nrK8fNisLi7irjB6X5gopnKpB', // Sharx by Sharky.fi
  '731niXDWZAjC7Cb33mHCsJn1F2CUHoy3nDdZs1u7kzLm', // Cogent Cogs
  '9uBX3ASjxWvNBAD1xjbVaKA74mWGZys3RGSF7DdeDD3F', // Hall of Junk (Junk.Fun reward NFTs)
].map(addr => addr.toLowerCase()));

export interface DasAsset {
  id: string; // mint address
  interface:
    | 'V1_NFT'
    | 'V1_PRINT'
    | 'LEGACY_NFT'
    | 'V2_NFT'
    | 'MPL_CORE_ASSET'
    | 'MplCoreAsset' // Helius DAS API returns this format
    | 'FungibleToken'
    | 'FungibleAsset'
    | 'Custom'
    | 'Identity'
    | 'Executable'
    | 'ProgrammableNFT';
  content?: {
    metadata?: {
      name?: string;
      symbol?: string;
    };
    files?: Array<{ uri: string; mime?: string }>;
    links?: {
      image?: string;
    };
  };
  token_info?: {
    symbol?: string;
    decimals?: number;
    token_program?: string;
    associated_token_address?: string;
    balance?: number;
    price_info?: {
      price_per_token?: number;
    };
  };
  ownership: {
    owner: string;
    delegated?: boolean; // Made optional as it might not always be present
    frozen?: boolean; // Made optional as it might not always be present
  };
  compression?: {
    compressed: boolean;
  };
  burnt?: boolean;
  grouping?: Array<{
    group_key: string;
    group_value: string;
  }>;
  // MPL Core plugin information (returned by Helius DAS API)
  plugins?: Record<string, unknown>;
  unknown_plugins?: Array<unknown>;
  mpl_core_info?: {
    plugins_json_version?: number;
  };
}

export interface BurnableAsset {
  mint: string;
  type: 'spl_token' | 'token_2022' | 'metadata_nft' | 'mpl_core_nft';
  name: string;
  symbol: string;
  decimals?: number;
  tokenAccount?: string;
  metadata?: string; // For metadata NFTs
  masterEdition?: string; // For metadata NFTs
  collection?: string; // Collection mint address (for both Metadata and Core NFTs)
  collectionMetadata?: string; // Collection metadata PDA (for Metadata NFTs only)
  collectionName?: string; // Human-readable collection name for UI display
  isNFT: boolean;
  imageUrl?: string;
  withheldAmount?: number; // For Token-2022 transfer fee tracking
  balance?: number; // Token balance (raw units)
  uiAmount?: number; // Human-readable balance
  isBlacklisted?: boolean; // Token is in blacklist - can only be swapped, not burned
  isSwapOnly?: boolean; // Token can only be swapped (e.g., FungibleAsset, Token-2022 with fees)
  /**
   * Live lamports a burn would return, read on-chain: the token account for
   * tokens; token account + metadata + master edition for Metaplex NFTs; the
   * asset account net of the non-reclaimable protocol floor for MPL Core NFTs.
   * Rent minimums change with SIMD-0437 and old accounts keep their old deposit,
   * so this is the only reliable basis for a "you will receive" estimate.
   */
  lamports?: number;
}

export type AssetDiscoverySource = 'das' | 'rpc_fallback';

export interface AssetDiscoveryDiagnostics {
  source: AssetDiscoverySource;
  interfaceCounts: Record<string, number>;
  dasFailed: boolean;
  nftDiscoveryUnavailable: boolean;
  dasError?: string;
}

export interface BurnableAssetsResult {
  assets: BurnableAsset[];
  diagnostics: AssetDiscoveryDiagnostics;
}

export interface GetBurnableAssetsOptions {
  skipMetadataEnrichment?: boolean;
}

const LARGE_WALLET_TOKEN_ENRICHMENT_THRESHOLD = 150;

function isValidPublicKey(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }

  try {
    new PublicKey(value);
    return true;
  } catch {
    return false;
  }
}

function getAssetGrouping(asset: DasAsset) {
  return Array.isArray(asset.grouping) ? asset.grouping : [];
}

function getCollectionGrouping(asset: DasAsset) {
  return getAssetGrouping(asset).find((group) => group?.group_key === 'collection');
}

interface DasFetchResult {
  assets: DasAsset[];
  error?: string;
}

function countInterfaces(assets: DasAsset[]): Record<string, number> {
  return assets.reduce<Record<string, number>>((counts, asset) => {
    counts[asset.interface] = (counts[asset.interface] || 0) + 1;
    return counts;
  }, {});
}

function filterValidAssets(assets: BurnableAsset[], sourceLabel: string): BurnableAsset[] {
  const validAssets = assets.filter((asset) => asset && asset.mint && asset.mint.trim() !== '');
  if (validAssets.length < assets.length) {
    logger.warn(
      `⚠️ Filtered out ${assets.length - validAssets.length} assets with invalid mint addresses from ${sourceLabel}`
    );
  }
  return validAssets;
}

/**
 * Get all assets for a wallet using DAS API with pagination
 * Using raw fetch for better compatibility across all RPC providers
 */
export async function getAllAssets(walletAddress: string, rpcUrl: string): Promise<DasFetchResult> {
  try {
    const allAssets: DasAsset[] = [];
    let page = 1;
    const limit = 1000; // Max limit per page
    let hasMore = true;

    // Fetch all pages using raw fetch (proven to work)
    while (hasMore) {
      const response = await fetch(rpcUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: `asset-list-page-${page}`,
          method: 'getAssetsByOwner',
          params: {
            ownerAddress: walletAddress,
            page,
            limit,
            displayOptions: {
              showFungible: true,
              showZeroBalance: true, // Include 0-balance tokens so users can reclaim rent
              // NOTE: Not using showCollectionMetadata as it may change response format
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`DAS API request failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(`DAS API error: ${data.error.message}`);
      }

      const items = data.result?.items || [];
      allAssets.push(...items);

      logger.log(`📄 Fetched page ${page}: ${items.length} assets (total: ${allAssets.length})`);

      // Check if there are more pages
      hasMore = items.length === limit;
      page++;

      // Safety limit: stop after 10 pages (10,000 assets)
      if (page > 10) {
        logger.warn('⚠️ Reached pagination limit (10 pages). Some assets may not be loaded.');
        break;
      }
    }

    logger.log(`✅ DAS API: Loaded ${allAssets.length} total assets`);
    return { assets: allAssets };
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Unknown DAS asset discovery error';
    logger.error('Failed to fetch assets from DAS API:', error);
    return {
      assets: [],
      error: message,
    };
  }
}

/**
 * Filter assets to only include burnable ones
 * Excludes: compressed NFTs, system tokens, invalid accounts, wrapped SOL, FungibleAsset tokens
 *
 * NOTE: FungibleAsset tokens are filtered out to prevent withheld fee errors with Token-2022.
 * Only FungibleToken interface tokens are processed.
 * Uses fallback detection for tokens that may not report correct interface.
 */
export async function filterBurnableAssets(assets: DasAsset[]): Promise<BurnableAsset[]> {
  const burnableAssets: BurnableAsset[] = [];
  const skippedCount = { compressed: 0, noOwnership: 0, frozen: 0, delegated: 0, burnt: 0, wrappedSol: 0, noTokenAccount: 0, zeroBalance: 0, withheldAmount: 0, unsupportedType: 0, fungibleAsset: 0, collectionNFT: 0 };

  /**
   * Helper function to safely convert any value to a string
   * Handles PublicKey objects that some wallets (like Backpack) return instead of strings
   */
  const ensureString = (value: any): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    // Convert PublicKey or any object with toString() method
    return String(value);
  };

  logger.log(`🔍 Filtering ${assets.length} DAS assets...`);

  // First pass: Collect all collection mint addresses
  // This helps us identify and skip collection NFTs that appear as fungible tokens
  const collectionMints = new Set<string>();
  for (const asset of assets) {
    const collectionMint = ensureString(getCollectionGrouping(asset)?.group_value);
    if (collectionMint && isValidPublicKey(collectionMint)) {
      collectionMints.add(collectionMint);
    } else if (collectionMint) {
      logger.warn(`⚠️ Ignoring invalid collection mint on asset ${ensureString(asset.id)?.slice(0, 8) || 'unknown'}...`);
    }
  }
  if (collectionMints.size > 0) {
    logger.log(`📋 Found ${collectionMints.size} collection(s) referenced by child NFTs`);
  }

  for (const asset of assets) {
    // Skip compressed NFTs (cannot be burned easily)
    if (asset.compression?.compressed) {
      skippedCount.compressed++;
      logger.log(`⏭️ Skipping compressed NFT: ${asset.id}`);
      continue;
    }

    // Skip if no ownership
    if (!asset.ownership?.owner) {
      skippedCount.noOwnership++;
      logger.log(`⏭️ Skipping asset with no ownership: ${asset.id}`);
      continue;
    }

    // Skip if frozen (explicit true check - field might be undefined)
    if (asset.ownership.frozen === true && asset.interface !== 'ProgrammableNFT') {
      skippedCount.frozen++;
      logger.log(`⏭️ Skipping frozen asset: ${asset.id}`);
      continue;
    }

    // Skip if delegated (explicit true check - field might be undefined)
    if (asset.ownership.delegated === true) {
      skippedCount.delegated++;
      logger.log(`⏭️ Skipping delegated asset: ${asset.id}`);
      continue;
    }

    // Skip if burnt (explicit true check - field might be undefined)
    if (asset.burnt === true) {
      skippedCount.burnt++;
      logger.log(`⏭️ Skipping burnt asset: ${asset.id}`);
      continue;
    }

    // Ensure mint is a string (some wallets like Backpack may return PublicKey objects)
    const mint = ensureString(asset.id);
    if (!mint) {
      skippedCount.noOwnership++;
      logger.warn(`⚠️ Asset has no valid mint address, skipping`);
      continue;
    }

    // Skip wrapped SOL (native SOL token) - always skip, never swappable
    if (mint === WRAPPED_SOL_MINT) {
      skippedCount.wrappedSol++;
      logger.log('⏭️ Skipping wrapped SOL');
      continue;
    }

    // Check if token is blacklisted (USDT, USDC, BONK, etc.)
    // Blacklisted tokens are included but marked - they can only be swapped, not burned
    const isBlacklisted = BLACKLISTED_TOKENS.includes(mint);
    if (isBlacklisted) {
      logger.log(`🛡️ Including blacklisted token (swap-only): ${asset.content?.metadata?.symbol || mint.slice(0, 8)}...`);
    }

    // Skip collection NFTs (they appear as FungibleToken but shouldn't be burned)
    // Collection NFTs are the parent tokens that child NFTs reference via grouping.collection
    if (collectionMints.has(mint)) {
      skippedCount.collectionNFT++;
      logger.log(`⏭️ Skipping collection NFT (parent collection token): ${asset.content?.metadata?.name || mint.slice(0, 8)}...`);
      continue;
    }

    const name = asset.content?.metadata?.name || 'Unknown';
    const symbol = asset.content?.metadata?.symbol || asset.token_info?.symbol || '???';

    if (asset.interface === 'ProgrammableNFT' && !ENABLE_PNFT_JUNKING) {
      skippedCount.unsupportedType++;
      logger.log(`⏭️ Skipping ProgrammableNFT until BurnV1 support is implemented: ${name} (${mint.slice(0, 8)}...)`);
      continue;
    }

    // Skip Hall of Junk NFTs (Junk.Fun reward NFTs should not be burnable)
    if (name.toLowerCase().includes('hall of junk')) {
      skippedCount.unsupportedType++;
      logger.log(`🛡️ Skipping Hall of Junk NFT: ${name} (${mint.slice(0, 8)}...)`);
      continue;
    }

    // Get image URL from DAS with fallback to placeholder
    // Filter out problematic domains like dexlab.space
    const rawImageUrl =
      asset.content?.links?.image ||
      asset.content?.files?.[0]?.uri ||
      '';

    // Apply validation to filter out blocked domains
    let imageUrl = getValidImageUrl(rawImageUrl) || '';

    // Validate URL format - must be http/https, local path, or data URL
    if (imageUrl) {
      if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/') && !imageUrl.startsWith('data:')) {
        logger.warn(`Invalid image URL from DAS for ${symbol}: ${imageUrl}`);
        imageUrl = '/token-placeholder.svg';
      }
    } else {
      imageUrl = '/token-placeholder.svg';
    }

    // FungibleAsset is just a fungible token with Metaplex metadata - it can use either
    // standard SPL Token or Token-2022. Only Token-2022 tokens with problematic extensions
    // (transfer fees, withheld amounts, etc.) should be marked as swap-only.
    const isFungibleAsset = asset.interface === 'FungibleAsset';

    // Process FungibleToken and FungibleAsset interface tokens
    // Both can be burned if they use standard SPL Token program or Token-2022 without problematic extensions
    if (asset.interface === 'FungibleToken' || isFungibleAsset) {
      // SPL Token or Token-2022
      const tokenProgram = asset.token_info?.token_program;
      const decimals = asset.token_info?.decimals || 0;
      const tokenAccount = ensureString(asset.token_info?.associated_token_address);

      if (!tokenAccount) {
        skippedCount.noTokenAccount++;
        logger.warn(`⚠️ Token ${mint.slice(0, 8)}... has no token account, skipping`);
        continue;
      }

      // NOTE: Collection tokens are already filtered by the collectionMints check at line 403.
      // We previously had a heuristic here (decimals=0, metadata, balance=1) but it was too
      // aggressive and caught legitimate tokens like soulbound tokens, membership tokens, etc.
      // The collectionMints set accurately identifies real collection parent tokens.

      // NOTE: Zero-balance tokens are INCLUDED because users can reclaim rent by closing them.
      // The on-chain validation (validateTokenAccountsOnChain) filters out accounts that have
      // already been closed. Pre-burn validation in useBurnAssets also catches stale data.
      if (asset.token_info?.balance !== undefined && asset.token_info.balance <= 0) {
        skippedCount.zeroBalance++;
        logger.log(`📌 Including zero-balance token (can reclaim rent): ${asset.content?.metadata?.symbol || mint.slice(0, 8)}...`);
        // Don't skip - validateTokenAccountsOnChain and pre-burn validation filter closed accounts
      }

      // Check for mint extensions that affect token operations
      const mintExtensions = (asset as any).mint_extensions;

      // Extensions that prevent transfers entirely - these tokens can't be swapped OR burned
      const UNSWAPPABLE_EXTENSIONS = [
        'permanent_delegate',
        'non_transferable',
      ];

      // Extensions that are risky for burning but may allow swapping
      // Most Token-2022 extensions don't actually prevent burning:
      // - transfer_fee_config: fees already collected, burning works
      // - close_authority: affects post-burn cleanup, not burning itself
      // - interest_bearing_config: display-only, doesn't affect burning
      // - transfer_hook: hook runs but doesn't block burning
      // - mint_close_authority: affects mint, not token accounts
      // Only confidential transfer extensions are truly risky (encrypted amounts)
      const SWAP_ONLY_EXTENSIONS = [
        'confidential_transfer_mint',
        'confidential_transfer_fee_config',
      ];

      // Only mark as swap-only if it has actual problematic extensions, not just because it's FungibleAsset
      let hasSwapOnlyExtension = false;

      if (mintExtensions) {
        const extensionKeys = Object.keys(mintExtensions);
        const unswappable = extensionKeys.filter(ext => UNSWAPPABLE_EXTENSIONS.includes(ext));
        const swapOnly = extensionKeys.filter(ext => SWAP_ONLY_EXTENSIONS.includes(ext));

        if (unswappable.length > 0) {
          skippedCount.withheldAmount++;
          logger.warn(`⚠️ Token ${symbol} (${mint.slice(0, 8)}...) has unswappable extensions, skipping`);
          logger.warn(`  Unswappable extensions found: ${unswappable.join(', ')}`);
          continue;
        }

        if (swapOnly.length > 0) {
          hasSwapOnlyExtension = true;
          logger.log(`🔄 Token ${symbol} (${mint.slice(0, 8)}...) has swap-only extensions: ${swapOnly.join(', ')}`);
        }
      }

      // Track withheld amount (transfer fees that have accumulated on the token account)
      // Note: Tokens with transfer_fee_config CAN be burned - the fees are already collected.
      // We track withheld amount for display purposes but it doesn't block burning.
      const withheldAmount = (asset as any).mint_extensions?.transfer_fee_config?.withheld_amount || 0;

      // Additional checks for Token-2022 specific characteristics
      if (tokenProgram === TOKEN_2022_PROGRAM_ID.toString()) {
        // Check for extensions that prevent transfers entirely
        const hasPermanentDelegate =
          (asset as any).mint_extensions?.permanent_delegate ||
          (asset as any).extensions?.permanent_delegate;
        const hasNonTransferable =
          (asset as any).mint_extensions?.non_transferable ||
          (asset as any).extensions?.non_transferable;

        if (hasPermanentDelegate || hasNonTransferable) {
          skippedCount.withheldAmount++;
          logger.warn(`⚠️ Token-2022 ${symbol} has transfer-blocking extensions, skipping`);
          continue;
        }
      }

      const type = tokenProgram === TOKEN_2022_PROGRAM_ID.toString() ? 'token_2022' : 'spl_token';

      logger.log(`✅ Adding token: ${symbol} (${mint.slice(0, 8)}...) | Account: ${tokenAccount.slice(0, 8)}...`);

      // Calculate UI amount for display
      const balance = asset.token_info?.balance;
      const uiAmount = balance !== undefined && decimals !== undefined
        ? balance / Math.pow(10, decimals)
        : undefined;

      burnableAssets.push({
        mint,
        type,
        name,
        symbol,
        decimals,
        tokenAccount,
        isNFT: false,
        imageUrl,
        withheldAmount,
        balance,
        uiAmount,
        isBlacklisted,
        isSwapOnly: hasSwapOnlyExtension,
      });
    } else if (
      asset.interface === 'V1_NFT' ||
      asset.interface === 'V2_NFT' ||
      asset.interface === 'LEGACY_NFT' ||
      asset.interface === 'V1_PRINT' ||
      asset.interface === 'ProgrammableNFT'
    ) {
      // Filter out burnt metadata NFTs (balance = 0 means the NFT was burned)
      if ((asset as any).token_info?.balance <= 0) {
        skippedCount.zeroBalance++;
        logger.log(`⏭️ Skipping burnt metadata NFT: ${name} (${mint.slice(0, 8)}...)`);
        continue;
      }

      // Metaplex Token Metadata NFT (all supported types)
      const mintPubkey = new PublicKey(mint);
      const METADATA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');

      // Derive metadata PDA
      const [metadataPda] = PublicKey.findProgramAddressSync(
        [Buffer.from('metadata'), METADATA_PROGRAM_ID.toBuffer(), mintPubkey.toBuffer()],
        METADATA_PROGRAM_ID
      );

      // Derive master edition PDA
      const [masterEditionPda] = PublicKey.findProgramAddressSync(
        [Buffer.from('metadata'), METADATA_PROGRAM_ID.toBuffer(), mintPubkey.toBuffer(), Buffer.from('edition')],
        METADATA_PROGRAM_ID
      );

      // Extract collection from grouping field
      const collectionMint = ensureString(getCollectionGrouping(asset)?.group_value);

      // Skip NFTs from protected collections (high-value blue chip collections)
      if (collectionMint && PROTECTED_NFT_COLLECTIONS.has(collectionMint.toLowerCase())) {
        skippedCount.unsupportedType++;
        logger.log(`🛡️ Skipping protected collection NFT: ${name} (${mint.slice(0, 8)}...)`);
        logger.log(`  └─ Protected Collection: ${collectionMint.slice(0, 8)}...`);
        continue;
      }

      // Derive collection metadata PDA if NFT has a collection (needed for Metaplex burning)
      let collectionMetadataPda: string | undefined;
      if (collectionMint && isValidPublicKey(collectionMint)) {
        const collectionPubkey = new PublicKey(collectionMint);
        const [collectionMetadata] = PublicKey.findProgramAddressSync(
          [Buffer.from('metadata'), METADATA_PROGRAM_ID.toBuffer(), collectionPubkey.toBuffer()],
          METADATA_PROGRAM_ID
        );
        collectionMetadataPda = collectionMetadata.toString();
      } else if (collectionMint) {
        logger.warn(`⚠️ NFT ${name} has invalid collection mint, skipping collection metadata PDA derivation`);
      }

      // Try to get collection name from the asset's grouping info
      const collectionGrouping = getCollectionGrouping(asset);
      const collectionName = (collectionGrouping as any)?.collection_metadata?.name || undefined;

      logger.log(`✅ Adding NFT: ${name} (${mint.slice(0, 8)}...)${collectionMint ? ` | Collection: ${collectionMint.slice(0, 8)}...` : ''}`);
      burnableAssets.push({
        mint,
        type: 'metadata_nft',
        name,
        symbol,
        tokenAccount: ensureString(asset.token_info?.associated_token_address),
        metadata: metadataPda.toString(),
        masterEdition: masterEditionPda.toString(),
        collection: collectionMint,
        collectionMetadata: collectionMetadataPda,
        collectionName,
        isNFT: true,
        imageUrl,
      });
    } else if (asset.interface === 'MPL_CORE_ASSET' || asset.interface === 'MplCoreAsset') {
      // Metaplex Core NFT (Helius DAS API returns 'MplCoreAsset')

      // Check for freeze-related plugins that prevent burning
      // The DAS API returns plugins as an object with plugin names as keys
      const plugins = asset.plugins && typeof asset.plugins === 'object' && !Array.isArray(asset.plugins) ? asset.plugins : {};
      const pluginNames = Object.keys(plugins);

      // Plugins that indicate the NFT cannot be burned
      const FREEZE_PLUGINS = [
        'permanentFreezeDelegate',
        'permanent_freeze_delegate',
        'freezeDelegate',
        'freeze_delegate',
      ];

      const hasFreezePlugin = pluginNames.some(pluginName =>
        FREEZE_PLUGINS.some(freezePlugin =>
          pluginName.toLowerCase().includes(freezePlugin.toLowerCase())
        )
      );

      // Also check if any freeze plugin has "frozen: true" state
      const isFrozenByPlugin = pluginNames.some(pluginName => {
        const plugin = plugins[pluginName] as any;
        return plugin?.frozen === true ||
               plugin?.authority?.type === 'None' || // permanent freeze with no authority to unfreeze
               pluginName.toLowerCase().includes('permanentfreeze');
      });

      if (hasFreezePlugin || isFrozenByPlugin) {
        skippedCount.frozen++;
        logger.log(`⏭️ Skipping MPL Core NFT with freeze plugin: ${name} (${mint.slice(0, 8)}...)`);
        logger.log(`  └─ Freeze plugins found: ${pluginNames.filter(p =>
          FREEZE_PLUGINS.some(fp => p.toLowerCase().includes(fp.toLowerCase()))
        ).join(', ') || 'frozen state detected'}`);
        continue;
      }

      // Extract collection from grouping field
      const collection = ensureString(getCollectionGrouping(asset)?.group_value);

      // Skip Core NFTs from protected collections (high-value blue chip collections)
      if (collection && PROTECTED_NFT_COLLECTIONS.has(collection.toLowerCase())) {
        skippedCount.unsupportedType++;
        logger.log(`🛡️ Skipping protected MPL Core NFT: ${name} (${mint.slice(0, 8)}...)`);
        logger.log(`  └─ Protected Collection: ${collection.slice(0, 8)}...`);
        continue;
      }

      // Try to get collection name from the asset's grouping info
      const collectionGrouping = getCollectionGrouping(asset);
      const collectionName = (collectionGrouping as any)?.collection_metadata?.name || undefined;

      logger.log(`✅ Adding MPL Core NFT: ${name} (${mint.slice(0, 8)}...)${collection ? ` | Collection: ${collection.slice(0, 8)}...` : ''}`);
      burnableAssets.push({
        mint,
        type: 'mpl_core_nft',
        name,
        symbol,
        collection,
        collectionName,
        isNFT: true,
        imageUrl,
      });
    } else {
      // FALLBACK: Check if it might be a token based on properties (not interface)
      // This catches tokens that don't report the correct interface type
      if (asset.token_info?.associated_token_address &&
          asset.token_info?.token_program &&
          (asset.token_info.token_program === TOKEN_PROGRAM_ID.toString() ||
           asset.token_info.token_program === TOKEN_2022_PROGRAM_ID.toString())) {

        const decimals = asset.token_info?.decimals || 0;
        const tokenAccount = ensureString(asset.token_info.associated_token_address);

        if (!tokenAccount) {
          skippedCount.noTokenAccount++;
          logger.warn(`⚠️ Token ${mint.slice(0, 8)}... has no valid token account (fallback), skipping`);
          continue;
        }

        // NOTE: We previously filtered tokens with decimals=0 and balance=1 as "likely NFTs"
        // but this was too aggressive and caught legitimate tokens like soulbound tokens,
        // membership tokens, etc. These tokens can be burned on-chain, so we allow them.
        // Real NFTs are handled by the specific NFT interface checks above.

        // NOTE: We still show tokens with 0 balance because users can reclaim rent from them
        // Track the zero balance for logging but don't skip them
        if (asset.token_info?.balance !== undefined && asset.token_info.balance <= 0) {
          skippedCount.zeroBalance++;
          logger.log(`📌 Including zero balance token (fallback, can reclaim rent): ${symbol}`);
          // Don't continue - let it be added to burnable assets
        }

        // Apply extension checks in fallback - aligned with main path
        // Only these extensions truly block burning:
        // - permanent_delegate: someone else controls the token
        // - non_transferable: can't move to burn
        // - confidential_transfer_*: encrypted amounts are risky
        // Other extensions (transfer_fee_config, close_authority, etc.) don't prevent burning
        const mintExtensions = (asset as any).mint_extensions;
        const DANGEROUS_EXTENSIONS = [
          'permanent_delegate',
          'non_transferable',
          'confidential_transfer_mint',
          'confidential_transfer_fee_config',
        ];

        if (mintExtensions) {
          const extensionKeys = Object.keys(mintExtensions);
          const dangerousExtensions = extensionKeys.filter(ext => DANGEROUS_EXTENSIONS.includes(ext));

          if (dangerousExtensions.length > 0) {
            skippedCount.withheldAmount++;
            logger.warn(`⚠️ Token ${symbol} has dangerous extensions (fallback), skipping`);
            continue;
          }
        }

        // Check for withheld amount (transfer fees that have accumulated)
        const withheldAmount = (asset as any).mint_extensions?.transfer_fee_config?.withheld_amount || 0;

        // Apply Token-2022 specific checks for truly blocking extensions
        if (asset.token_info.token_program === TOKEN_2022_PROGRAM_ID.toString()) {
          const hasBlockingExtension =
            (asset as any).mint_extensions?.permanent_delegate ||
            (asset as any).mint_extensions?.non_transferable ||
            (asset as any).extensions?.permanent_delegate ||
            (asset as any).extensions?.non_transferable;

          if (hasBlockingExtension) {
            skippedCount.withheldAmount++;
            logger.warn(`⚠️ Token-2022 ${symbol} has blocking extensions (fallback), skipping`);
            continue;
          }
        }

        const type = asset.token_info.token_program === TOKEN_2022_PROGRAM_ID.toString() ? 'token_2022' : 'spl_token';

        logger.log(`✅ Adding token via fallback detection: ${symbol} (${mint.slice(0, 8)}...) | Interface: ${asset.interface}`);

        // Calculate UI amount for display
        const balance = asset.token_info?.balance;
        const uiAmount = balance !== undefined && decimals !== undefined
          ? balance / Math.pow(10, decimals)
          : undefined;

        burnableAssets.push({
          mint,
          type,
          name,
          symbol,
          decimals,
          tokenAccount,
          isNFT: false,
          imageUrl,
          withheldAmount,
          balance,
          uiAmount,
        });
      } else {
        skippedCount.unsupportedType++;
        logger.log(`⏭️ Skipping unsupported asset type: ${asset.interface} | ${mint.slice(0, 8)}...`);
      }
    }
  }

  logger.log(`📊 Filtering Summary:`);
  logger.log(`  ✅ Burnable assets: ${burnableAssets.length}`);
  logger.log(`  ⏭️ Skipped - Compressed: ${skippedCount.compressed}`);
  logger.log(`  ⏭️ Skipped - No ownership: ${skippedCount.noOwnership}`);
  logger.log(`  ⏭️ Skipped - Frozen: ${skippedCount.frozen}`);
  logger.log(`  ⏭️ Skipped - Delegated: ${skippedCount.delegated}`);
  logger.log(`  ⏭️ Skipped - Burnt: ${skippedCount.burnt}`);
  logger.log(`  ⏭️ Skipped - Wrapped SOL/Blacklisted: ${skippedCount.wrappedSol}`);
  logger.log(`  ⏭️ Skipped - No token account: ${skippedCount.noTokenAccount}`);
  logger.log(`  📌 Included - Zero balance (can reclaim rent): ${skippedCount.zeroBalance}`);
  logger.log(`  ⏭️ Skipped - Withheld amount: ${skippedCount.withheldAmount}`);
  if (skippedCount.fungibleAsset > 0) {
    logger.log(`  ⏭️ Skipped - FungibleAsset tokens (not supported): ${skippedCount.fungibleAsset}`);
  }
  if (skippedCount.collectionNFT > 0) {
    logger.log(`  ⏭️ Skipped - Collection NFTs (parent tokens): ${skippedCount.collectionNFT}`);
  }
  logger.log(`  ⏭️ Skipped - Unsupported type: ${skippedCount.unsupportedType}`);

  return burnableAssets;
}

/**
 * Enrich assets with metadata from Jupiter/Solana token lists
 * Used when DAS API doesn't provide metadata (QuickNode, standard RPC)
 */
async function enrichMetadataForAssets(
  assets: BurnableAsset[],
  network: 'mainnet' | 'devnet'
): Promise<BurnableAsset[]> {
  // Find assets that need enrichment - only tokens with missing or invalid metadata
  const assetsNeedingEnrichment = assets.filter(
    (asset) =>
      !asset.isNFT && // Only enrich tokens, not NFTs
      (asset.symbol === '???' ||
       asset.symbol === asset.mint.slice(0, 8) || // Looks like fallback symbol
       asset.name === 'Unknown' ||
       asset.name.startsWith('Token ') || // Looks like fallback name
       asset.imageUrl === '/token-placeholder.svg' ||
       !asset.imageUrl ||
       // Also enrich if imageUrl looks invalid (not a proper URL format)
       (asset.imageUrl && !asset.imageUrl.startsWith('http') && !asset.imageUrl.startsWith('/') && !asset.imageUrl.startsWith('data:')))
  );

  if (assetsNeedingEnrichment.length === 0) {
    logger.log('✅ All assets already have metadata, skipping enrichment');
    return assets;
  }

  const allowFallbackLookups = assetsNeedingEnrichment.length <= LARGE_WALLET_TOKEN_ENRICHMENT_THRESHOLD;

  if (!allowFallbackLookups) {
    logger.warn(
      `⚠️ Skipping expensive per-token metadata fallback for ${assetsNeedingEnrichment.length} tokens; using DAS/RPC metadata only`
    );
  }

  logger.log(`🔧 Enriching metadata for ${assetsNeedingEnrichment.length} tokens missing info...`);

  // Enrich each asset
  let enrichedCount = 0;
  for (const asset of assetsNeedingEnrichment) {
    try {
      const metadata = await getEnhancedTokenMetadata(asset.mint, network, {
        allowFallbackLookups,
      });

      if (metadata) {
        // Update asset with enriched metadata
        // Always update with better metadata if available
        if (metadata.symbol) {
          asset.symbol = metadata.symbol;
        }
        if (metadata.name) {
          asset.name = metadata.name;
        }
        // Always prefer enriched images as they're more reliable
        if (metadata.logoURI) {
          // Apply validation to filter out blocked domains
          const validImageUrl = getValidImageUrl(metadata.logoURI);

          if (validImageUrl) {
            // Check if it's a valid URL format
            if (validImageUrl.startsWith('http') || validImageUrl.startsWith('/') || validImageUrl.startsWith('data:')) {
              asset.imageUrl = validImageUrl;
            } else {
              // Invalid URL format, use placeholder
              asset.imageUrl = '/token-placeholder.svg';
            }
          } else {
            // Blocked domain or invalid, use placeholder
            asset.imageUrl = '/token-placeholder.svg';
          }
        } else if (!asset.imageUrl || asset.imageUrl === '/token-placeholder.svg') {
          // No enriched image and no existing valid image, ensure placeholder
          asset.imageUrl = '/token-placeholder.svg';
        }
        enrichedCount++;
        logger.log(`  ✅ Enriched ${metadata.symbol || asset.symbol} (${asset.mint.slice(0, 8)}...)`);
      }
    } catch (error) {
      logger.warn(`  ⚠️ Failed to enrich ${asset.mint.slice(0, 8)}...`, error);
    }
  }

  logger.log(`✅ Successfully enriched ${enrichedCount}/${assetsNeedingEnrichment.length} tokens`);
  return assets;
}

/** Metaplex Core keeps a fixed protocol fee in the asset account after BurnV1. */
const MPL_CORE_PROTOCOL_FEE_LAMPORTS = 1_500_000;

/**
 * Attach the live lamports a burn would return to each NFT.
 * Best-effort: on RPC failure the NFTs are returned unchanged (estimate shows 0).
 */
async function annotateNftLamports(
  nfts: BurnableAsset[],
  connection: Connection
): Promise<BurnableAsset[]> {
  if (nfts.length === 0) return nfts;

  // Which accounts does the program close for each NFT type?
  const accountsFor = (asset: BurnableAsset): string[] => {
    if (asset.type === 'metadata_nft') {
      return [asset.tokenAccount, asset.metadata, asset.masterEdition].filter(
        (a): a is string => !!a && isValidPublicKey(a)
      );
    }
    if (asset.type === 'mpl_core_nft') {
      return isValidPublicKey(asset.mint) ? [asset.mint] : [];
    }
    return [];
  };

  const uniqueAddresses = Array.from(new Set(nfts.flatMap(accountsFor)));
  if (uniqueAddresses.length === 0) return nfts;

  try {
    const lamportsByAddress = new Map<string, number>();
    const BATCH_SIZE = 100;
    for (let i = 0; i < uniqueAddresses.length; i += BATCH_SIZE) {
      const batch = uniqueAddresses.slice(i, i + BATCH_SIZE);
      const infos = await connection.getMultipleAccountsInfo(batch.map((a) => new PublicKey(a)));
      infos.forEach((info, idx) => lamportsByAddress.set(batch[idx], info?.lamports ?? 0));
    }

    // BurnV1 shrinks a Core asset to 1 byte and leaves the protocol fee in it, so only
    // the balance above that floor comes back. The 1-byte minimum moves with SIMD-0437.
    const hasCore = nfts.some((a) => a.type === 'mpl_core_nft');
    const coreFloor = hasCore
      ? (await connection.getMinimumBalanceForRentExemption(1)) + MPL_CORE_PROTOCOL_FEE_LAMPORTS
      : 0;

    return nfts.map((asset) => {
      const accounts = accountsFor(asset);
      if (accounts.length === 0) return asset;
      const live = accounts.reduce((sum, a) => sum + (lamportsByAddress.get(a) ?? 0), 0);
      const lamports = asset.type === 'mpl_core_nft' ? Math.max(0, live - coreFloor) : live;
      return { ...asset, lamports };
    });
  } catch (error) {
    logger.warn('Failed to read NFT account balances for rent estimates:', error);
    return nfts;
  }
}

/**
 * Validate token accounts on-chain to filter out closed/invalid accounts
 * The DAS API can return stale data for accounts that have been closed
 * This prevents "InvalidAccountData" errors when trying to burn
 */
async function validateTokenAccountsOnChain(
  assets: BurnableAsset[],
  connection: Connection
): Promise<BurnableAsset[]> {
  if (assets.length === 0) return assets;

  // Only validate non-NFT tokens (NFTs use different account structures)
  const tokensToValidate = assets.filter((asset) => !asset.isNFT && asset.tokenAccount);
  const nfts = assets.filter(a => a.isNFT || !a.tokenAccount);

  if (tokensToValidate.length === 0) return annotateNftLamports(nfts, connection);

  logger.log(`🔍 Validating ${tokensToValidate.length} token accounts on-chain...`);

  try {
    // Batch fetch account info (max 100 at a time)
    const BATCH_SIZE = 100;
    const validTokens: BurnableAsset[] = [];
    let invalidCount = 0;
    const safeTokensToValidate = tokensToValidate.filter((asset) => {
      const isValid = isValidPublicKey(asset.tokenAccount);
      if (!isValid) {
        invalidCount++;
        logger.warn(`⚠️ Token account is not a valid public key: ${asset.symbol} (${asset.tokenAccount || 'unknown'})`);
      }
      return isValid;
    });

    for (let i = 0; i < safeTokensToValidate.length; i += BATCH_SIZE) {
      const batch = safeTokensToValidate.slice(i, i + BATCH_SIZE);
      const pubkeys = batch.map(a => new PublicKey(a.tokenAccount!));

      const accountInfos = await connection.getMultipleAccountsInfo(pubkeys);

      for (let j = 0; j < batch.length; j++) {
        const asset = batch[j];
        const accountInfo = accountInfos[j];

        // Check if account exists and has valid data
        if (!accountInfo) {
          logger.warn(`⚠️ Token account closed (no data): ${asset.symbol} (${asset.tokenAccount?.slice(0, 8)}...)`);
          invalidCount++;
          continue;
        }

        // SPL Token and Token-2022 base account size is 165 bytes
        // Token-2022 with extensions will be larger, but base must be at least 165
        const MIN_TOKEN_ACCOUNT_SIZE = 165;
        if (accountInfo.data.length < MIN_TOKEN_ACCOUNT_SIZE) {
          logger.warn(`⚠️ Token account data too small (${accountInfo.data.length} bytes): ${asset.symbol} (${asset.tokenAccount?.slice(0, 8)}...)`);
          invalidCount++;
          continue;
        }

        // Account is valid - keep its live rent deposit for estimates
        validTokens.push({ ...asset, lamports: accountInfo.lamports });
      }
    }

    if (invalidCount > 0) {
      logger.log(`⚠️ Filtered out ${invalidCount} invalid/closed token accounts`);
    }
    logger.log(`✅ ${validTokens.length} token accounts validated on-chain`);

    return [...validTokens, ...(await annotateNftLamports(nfts, connection))];
  } catch (error) {
    logger.error('Failed to validate token accounts on-chain:', error);
    // On error, return only NFTs (which weren't validated but are more reliable)
    // Don't return potentially invalid tokens that could cause transaction failures
    logger.warn(`⚠️ Validation failed - returning ${nfts.length} NFTs only (filtering out ${tokensToValidate.length} unvalidated tokens)`);
    return nfts;
  }
}

/**
 * Fallback method: Get SPL tokens using standard RPC
 * Used when DAS API is not available
 * Excludes wrapped SOL and empty accounts
 */
export async function getTokenAccountsStandard(
  walletAddress: string,
  connection: Connection
): Promise<BurnableAsset[]> {
  try {
    const publicKey = new PublicKey(walletAddress);

    // Get SPL Token accounts
    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(publicKey, {
      programId: TOKEN_PROGRAM_ID,
    });

    // Get Token-2022 accounts
    const token2022Accounts = await connection.getParsedTokenAccountsByOwner(publicKey, {
      programId: TOKEN_2022_PROGRAM_ID,
    });

    const burnableAssets: BurnableAsset[] = [];

    // Process SPL tokens (including zero-balance accounts)
    for (const { pubkey, account } of tokenAccounts.value) {
      const parsedInfo = account.data.parsed.info;
      const mint = parsedInfo.mint;
      const amount = parsedInfo.tokenAmount.uiAmount;

      // Skip wrapped SOL
      if (mint === WRAPPED_SOL_MINT) {
        logger.log('⏭️ Skipping wrapped SOL (standard RPC)');
        continue;
      }

      // Check if token is blacklisted (we'll include it but mark it for swap-only)
      const isBlacklisted = BLACKLISTED_TOKENS.includes(mint);
      if (isBlacklisted) {
        logger.log(`🏷️ Blacklisted token found: ${mint.slice(0, 8)}... - marking as swap-only (standard RPC)`);
      }

      // Skip frozen token accounts
      if (parsedInfo.state === 'frozen') {
        logger.log(`⏭️ Skipping frozen token account: ${mint.slice(0, 8)}... (${pubkey.toString().slice(0, 8)}...)`);
        continue;
      }

      // NOTE: We previously filtered tokens with decimals=0, balance=1 as "likely collection tokens"
      // but this was too aggressive and caught legitimate tokens. Collection tokens are now
      // identified by the collectionMints set in the DAS API path. In standard RPC fallback,
      // we allow these tokens through - the on-chain validation will catch any issues.

      logger.log(`  📍 SPL Token: ${mint.slice(0, 8)}... | Balance: ${amount}`);

      // Use a better fallback name - show more of the mint for identification
      const shortMint = mint.slice(0, 8);
      burnableAssets.push({
        mint,
        type: 'spl_token',
        name: `Token ${shortMint}`,  // Better fallback name
        symbol: shortMint,  // Use first 8 chars as symbol instead of 6
        decimals: parsedInfo.tokenAmount.decimals,
        tokenAccount: pubkey.toString(),
        isNFT: false,
        imageUrl: '/token-placeholder.svg',  // Ensure we have a fallback image
        balance: parsedInfo.tokenAmount.amount,
        uiAmount: amount,
        isBlacklisted,  // Mark blacklisted tokens - can only be swapped, not burned
      });
    }

    // Process Token-2022 tokens
    // Most Token-2022 extensions don't prevent burning - only permanent_delegate and
    // non_transferable truly block it. Standard RPC doesn't provide extension info,
    // but the on-chain validation and pre-burn checks will catch any issues.
    for (const { pubkey, account } of token2022Accounts.value) {
      const parsedInfo = account.data.parsed.info;
      const mint = parsedInfo.mint;
      const amount = parsedInfo.tokenAmount.uiAmount;

      // Skip wrapped SOL (though unlikely in Token-2022)
      if (mint === WRAPPED_SOL_MINT) {
        logger.log('⏭️ Skipping wrapped SOL in Token-2022');
        continue;
      }

      // Check if token is blacklisted
      const isBlacklisted = BLACKLISTED_TOKENS.includes(mint);
      if (isBlacklisted) {
        logger.log(`🏷️ Blacklisted token found: ${mint.slice(0, 8)}... in Token-2022 - marking as swap-only`);
      }

      // Skip frozen token accounts
      if (parsedInfo.state === 'frozen') {
        logger.log(`⏭️ Skipping frozen Token-2022 account: ${mint.slice(0, 8)}... (${pubkey.toString().slice(0, 8)}...)`);
        continue;
      }

      logger.log(`  📍 Token-2022: ${mint.slice(0, 8)}... | Balance: ${amount}`);

      // Use a better fallback name - show more of the mint for identification
      const shortMint = mint.slice(0, 8);
      burnableAssets.push({
        mint,
        type: 'token_2022',
        name: `Token ${shortMint}`,  // Better fallback name
        symbol: shortMint,  // Use first 8 chars as symbol instead of 6
        decimals: parsedInfo.tokenAmount.decimals,
        tokenAccount: pubkey.toString(),
        isNFT: false,
        imageUrl: '/token-placeholder.svg',  // Ensure we have a fallback image
        balance: parsedInfo.tokenAmount.amount,
        uiAmount: amount,
        isBlacklisted,  // Mark blacklisted tokens - can only be swapped, not burned
      });
    }

    logger.log(`✅ Found ${burnableAssets.length} burnable tokens via standard RPC (including zero-balance and blacklisted)`);
    return burnableAssets;
  } catch (error) {
    logger.error('Failed to fetch token accounts via standard RPC:', error);
    return [];
  }
}

/**
 * Main function to get all burnable assets with fallback
 */
export async function getBurnableAssets(
  walletAddress: string,
  rpcUrl: string,
  connection: Connection,
  options: GetBurnableAssetsOptions = {}
): Promise<BurnableAssetsResult> {
  logger.log(`🔎 Fetching assets for wallet: ${walletAddress.slice(0, 8)}...`);

  // Detect network from RPC URL
  const network = getNetworkFromRpcUrl(rpcUrl);
  const skipMetadataEnrichment = options.skipMetadataEnrichment === true;

  // Try DAS API first (Helius, Metaplex RPC, etc.)
  const dasResult = await getAllAssets(walletAddress, rpcUrl);
  const interfaceCounts = countInterfaces(dasResult.assets);
  logger.log(`📦 DAS interface counts: ${JSON.stringify(interfaceCounts)}`);

  // Try standard RPC fallback as well to compare
  const rpcAssets = await getTokenAccountsStandard(walletAddress, connection);

  if (dasResult.error) {
    logger.warn(`⚠️ DAS asset discovery failed, falling back to standard RPC tokens only: ${dasResult.error}`);
    let validRpcAssets = filterValidAssets(rpcAssets, 'RPC fallback results');

    if (!skipMetadataEnrichment) {
      validRpcAssets = await enrichMetadataForAssets(validRpcAssets, network);
    }

    validRpcAssets = await validateTokenAccountsOnChain(validRpcAssets, connection);

    return {
      assets: validRpcAssets,
      diagnostics: {
        source: 'rpc_fallback',
        interfaceCounts,
        dasFailed: true,
        nftDiscoveryUnavailable: true,
        dasError: dasResult.error,
      },
    };
  }

  if (dasResult.assets.length > 0) {
    // Always show DAS API status (even in production) for debugging
    logger.log(`📦 DAS API returned ${dasResult.assets.length} raw assets`);
    let dasFiltered = await filterBurnableAssets(dasResult.assets);

    // Enrich metadata for tokens missing info from DAS API
    if (!skipMetadataEnrichment) {
      dasFiltered = await enrichMetadataForAssets(dasFiltered, network);
    }

    logger.log(`📦 Standard RPC returned ${rpcAssets.length} tokens`);

    // If RPC found more tokens than DAS, merge results
    if (rpcAssets.length > dasFiltered.length) {
      logger.log(`⚠️ RPC found more tokens (${rpcAssets.length}) than DAS (${dasFiltered.length}), merging...`);
      
      // Merge results, avoiding duplicates by mint address
      const mintSet = new Set(dasFiltered.map(a => a.mint));
      const merged = [...dasFiltered];
      
      for (const rpcAsset of rpcAssets) {
        if (!mintSet.has(rpcAsset.mint)) {
          logger.log(`➕ Adding missing token from RPC: ${rpcAsset.symbol} (${rpcAsset.mint.slice(0, 8)}...)`);
          merged.push(rpcAsset);
          mintSet.add(rpcAsset.mint);
        }
      }

      logger.log(`✅ Total burnable assets after merge: ${merged.length}`);

      let validMerged = filterValidAssets(merged, 'merged results');

      // Enrich merged results (for tokens added from RPC)
      if (!skipMetadataEnrichment) {
        validMerged = await enrichMetadataForAssets(validMerged, network);
      }

      // Validate token accounts on-chain to filter out closed/invalid accounts
      validMerged = await validateTokenAccountsOnChain(validMerged, connection);

      return {
        assets: validMerged,
        diagnostics: {
          source: 'das',
          interfaceCounts,
          dasFailed: false,
          nftDiscoveryUnavailable: false,
        },
      };
    }

    let validDasFiltered = filterValidAssets(dasFiltered, 'DAS results');

    // Validate token accounts on-chain to filter out closed/invalid accounts
    validDasFiltered = await validateTokenAccountsOnChain(validDasFiltered, connection);

    return {
      assets: validDasFiltered,
      diagnostics: {
        source: 'das',
        interfaceCounts,
        dasFailed: false,
        nftDiscoveryUnavailable: false,
      },
    };
  }

  logger.log('📦 DAS API returned 0 assets');
  let validRpcAssets = filterValidAssets(rpcAssets, 'RPC results');
  if (!skipMetadataEnrichment) {
    validRpcAssets = await enrichMetadataForAssets(validRpcAssets, network);
  }
  validRpcAssets = await validateTokenAccountsOnChain(validRpcAssets, connection);
  return {
    assets: validRpcAssets,
    diagnostics: {
      source: 'das',
      interfaceCounts,
      dasFailed: false,
      nftDiscoveryUnavailable: false,
    },
  };
}

/**
 * Swappable token type for dust swapping
 */
export interface SwappableToken {
  mint: string;
  tokenAccount: string;
  balance: number;
  decimals: number;
  symbol: string;
  name: string;
  imageUrl?: string;
  uiAmount?: number;
}

/**
 * Filter burnable assets to only include tokens that can be swapped via Jupiter
 *
 * Criteria for swappable tokens:
 * - Must be a fungible token (not NFT)
 * - Must have a positive balance (zero-balance can't be swapped)
 * - Must have a valid token account
 * - Must not be Token-2022 with problematic extensions (already filtered in filterBurnableAssets)
 *
 * @param assets - Array of burnable assets from getBurnableAssets
 * @returns Array of tokens eligible for dust swapping
 */
export function getSwappableTokens(assets: BurnableAsset[]): SwappableToken[] {
  const swappable: SwappableToken[] = [];

  for (const asset of assets) {
    // Skip NFTs - only fungible tokens can be swapped
    if (asset.isNFT) {
      logger.log(`⏭️ [SWAP] Skipping NFT: ${asset.name}`);
      continue;
    }

    // Skip tokens without token account
    if (!asset.tokenAccount) {
      logger.log(`⏭️ [SWAP] Skipping token without account: ${asset.symbol}`);
      continue;
    }

    // Skip zero or negative balance tokens (can't swap nothing)
    if (!asset.balance || asset.balance <= 0) {
      logger.log(`⏭️ [SWAP] Skipping zero-balance token: ${asset.symbol}`);
      continue;
    }

    // Add to swappable list
    swappable.push({
      mint: asset.mint,
      tokenAccount: asset.tokenAccount,
      balance: asset.balance,
      decimals: asset.decimals || 0,
      symbol: asset.symbol,
      name: asset.name,
      imageUrl: asset.imageUrl,
      uiAmount: asset.uiAmount,
    });
  }

  logger.log(`✅ [SWAP] Found ${swappable.length} swappable tokens out of ${assets.length} assets`);

  return swappable;
}
